import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiPlus, FiSave, FiAlertCircle } = FiIcons;

function LogEntry({ entries, addEntry, logResults }) {
  const location = useLocation();
  const [isNewEntry, setIsNewEntry] = useState(true);
  const [selectedEntryId, setSelectedEntryId] = useState('');
  const [formData, setFormData] = useState({
    content_or_touchpoint: '',
    platform_or_location: '',
    engagement_rate: '',
    website_clicks: '',
    replies_or_dms: '',
    response_quality: '',
    notes: ''
  });
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState('');

  // Handle navigation from Dashboard or Navbar
  useEffect(() => {
    if (location.state?.entryId && entries.length > 0) {
      const entry = entries.find(e => e.id === location.state.entryId);
      if (entry) {
        setIsNewEntry(false);
        setSelectedEntryId(entry.id);
        setFormData({
          content_or_touchpoint: entry.content_or_touchpoint,
          platform_or_location: entry.platform_or_location,
          engagement_rate: entry.engagement_rate,
          website_clicks: entry.website_clicks,
          replies_or_dms: entry.replies_or_dms,
          response_quality: entry.response_quality,
          notes: entry.notes || ''
        });
        setSuccess('');
        setErrors({});
      }
    } else if (location.state?.createNew) {
      setIsNewEntry(true);
      setSelectedEntryId('');
      setFormData({
        content_or_touchpoint: '',
        platform_or_location: '',
        engagement_rate: '',
        website_clicks: '',
        replies_or_dms: '',
        response_quality: '',
        notes: ''
      });
      setSuccess('');
      setErrors({});
    }
  }, [location.state, entries]);

  const resetForm = () => {
    setFormData({
      content_or_touchpoint: '',
      platform_or_location: '',
      engagement_rate: '',
      website_clicks: '',
      replies_or_dms: '',
      response_quality: '',
      notes: ''
    });
    setErrors({});
    setSuccess('');
  };

  const handleEntryModeChange = (mode) => {
    setIsNewEntry(mode === 'new');
    resetForm();
    setSelectedEntryId('');
  };

  const handleEntrySelect = (entryId) => {
    setSelectedEntryId(entryId);
    const entry = entries.find(e => e.id === entryId);
    if (entry) {
      setFormData({
        content_or_touchpoint: entry.content_or_touchpoint,
        platform_or_location: entry.platform_or_location,
        engagement_rate: entry.engagement_rate,
        website_clicks: entry.website_clicks,
        replies_or_dms: entry.replies_or_dms,
        response_quality: entry.response_quality,
        notes: entry.notes || ''
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.content_or_touchpoint.trim()) {
      newErrors.content_or_touchpoint = 'Content/Touchpoint is required';
    }

    if (!formData.platform_or_location.trim()) {
      newErrors.platform_or_location = 'Platform/Location is required';
    }

    if (formData.engagement_rate === '' || formData.engagement_rate < 0 || formData.engagement_rate > 100) {
      newErrors.engagement_rate = 'Engagement rate must be between 0-100';
    }

    if (formData.website_clicks === '' || formData.website_clicks < 0) {
      newErrors.website_clicks = 'Website clicks must be 0 or greater';
    }

    if (formData.replies_or_dms === '' || formData.replies_or_dms < 0) {
      newErrors.replies_or_dms = 'Replies/DMs must be 0 or greater';
    }

    if (formData.response_quality === '' || formData.response_quality < 1 || formData.response_quality > 10) {
      newErrors.response_quality = 'Response quality must be between 1-10';
    }

    return newErrors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setSuccess('');
      return;
    }

    const submitData = {
      ...formData,
      engagement_rate: parseFloat(formData.engagement_rate),
      website_clicks: parseInt(formData.website_clicks),
      replies_or_dms: parseInt(formData.replies_or_dms),
      response_quality: parseInt(formData.response_quality)
    };

    if (isNewEntry) {
      const newId = addEntry(submitData);
      setSuccess('New entry created successfully!');
      resetForm();
    } else {
      const selectedEntry = entries.find(e => e.id === selectedEntryId);
      if (selectedEntry.log_count >= 5) {
        setErrors({ general: "You've reached the maximum of 5 logs for this item." });
        setSuccess('');
        return;
      }
      
      logResults(selectedEntryId, submitData);
      setSuccess(`Results logged successfully! Log count: ${selectedEntry.log_count + 1}/5`);
    }

    setErrors({});
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const availableEntries = entries.filter(entry => entry.log_count < 5);
  const selectedEntry = entries.find(e => e.id === selectedEntryId);

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Log Results</h1>
        <p className="mt-2 text-gray-600">Track engagement and response data for your content and touchpoints.</p>
      </div>

      {/* Entry Mode Selection */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <div className="flex flex-col sm:flex-row gap-4">
          <button
            onClick={() => handleEntryModeChange('new')}
            className={`flex items-center justify-center space-x-2 px-4 py-2 rounded-lg font-medium transition-colors ${
              isNewEntry
                ? 'bg-purple-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <SafeIcon icon={FiPlus} className="w-4 h-4" />
            <span>Create New Entry</span>
          </button>
          
          <button
            onClick={() => handleEntryModeChange('existing')}
            className={`flex items-center justify-center space-x-2 px-4 py-2 rounded-lg font-medium transition-colors ${
              !isNewEntry
                ? 'bg-purple-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <SafeIcon icon={FiSave} className="w-4 h-4" />
            <span>Log to Existing Entry</span>
          </button>
        </div>

        {!isNewEntry && (
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Entry to Update
            </label>
            <select
              value={selectedEntryId}
              onChange={(e) => handleEntrySelect(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            >
              <option value="">Choose an entry...</option>
              {availableEntries.map(entry => (
                <option key={entry.id} value={entry.id}>
                  {entry.content_or_touchpoint} - {entry.platform_or_location} (Logs: {entry.log_count}/5)
                </option>
              ))}
            </select>
            
            {!isNewEntry && availableEntries.length === 0 && (
              <p className="mt-2 text-sm text-yellow-600">
                All entries have reached the maximum of 5 logs. Create a new entry to continue tracking.
              </p>
            )}

            {selectedEntry && selectedEntry.log_count >= 4 && (
              <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-center space-x-2">
                  <SafeIcon icon={FiAlertCircle} className="w-4 h-4 text-yellow-600" />
                  <span className="text-sm text-yellow-700">
                    Warning: This entry has {selectedEntry.log_count}/5 logs. 
                    {selectedEntry.log_count === 4 ? ' This will be the final log.' : ''}
                  </span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Success Message */}
      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-green-400 rounded-full"></div>
            <span className="text-green-700 font-medium">{success}</span>
          </div>
        </div>
      )}

      {/* Error Messages */}
      {errors.general && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <SafeIcon icon={FiAlertCircle} className="w-4 h-4 text-red-600" />
            <span className="text-red-700 font-medium">{errors.general}</span>
          </div>
        </div>
      )}

      {/* Form */}
      {(isNewEntry || selectedEntryId) && (
        <form onSubmit={handleSubmit} className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Content/Touchpoint *
              </label>
              <input
                type="text"
                value={formData.content_or_touchpoint}
                onChange={(e) => handleInputChange('content_or_touchpoint', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 ${
                  errors.content_or_touchpoint ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="e.g., Instagram Reel about productivity tips"
              />
              {errors.content_or_touchpoint && (
                <p className="mt-1 text-sm text-red-600">{errors.content_or_touchpoint}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Platform/Location *
              </label>
              <input
                type="text"
                value={formData.platform_or_location}
                onChange={(e) => handleInputChange('platform_or_location', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 ${
                  errors.platform_or_location ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="e.g., Instagram, Email, Website, DM"
              />
              {errors.platform_or_location && (
                <p className="mt-1 text-sm text-red-600">{errors.platform_or_location}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Engagement Rate (%) *
              </label>
              <input
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={formData.engagement_rate}
                onChange={(e) => handleInputChange('engagement_rate', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 ${
                  errors.engagement_rate ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="0-100"
              />
              {errors.engagement_rate && (
                <p className="mt-1 text-sm text-red-600">{errors.engagement_rate}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Website Clicks *
              </label>
              <input
                type="number"
                min="0"
                value={formData.website_clicks}
                onChange={(e) => handleInputChange('website_clicks', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 ${
                  errors.website_clicks ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="0"
              />
              {errors.website_clicks && (
                <p className="mt-1 text-sm text-red-600">{errors.website_clicks}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Replies or DMs *
              </label>
              <input
                type="number"
                min="0"
                value={formData.replies_or_dms}
                onChange={(e) => handleInputChange('replies_or_dms', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 ${
                  errors.replies_or_dms ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="0"
              />
              {errors.replies_or_dms && (
                <p className="mt-1 text-sm text-red-600">{errors.replies_or_dms}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Response Quality (1-10) *
              </label>
              <input
                type="number"
                min="1"
                max="10"
                value={formData.response_quality}
                onChange={(e) => handleInputChange('response_quality', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 ${
                  errors.response_quality ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="1-10"
              />
              {errors.response_quality && (
                <p className="mt-1 text-sm text-red-600">{errors.response_quality}</p>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes
            </label>
            <textarea
              rows={4}
              value={formData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              placeholder="Additional observations, insights, or context..."
            />
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              className="bg-purple-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 transition-colors"
            >
              {isNewEntry ? 'Create Entry' : 'Log Results'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

export default LogEntry;